import { Box, TextField, Paper, List, ListItemButton, ListItemText, CircularProgress, Typography } from "@mui/material";
import { LocationOn } from "@mui/icons-material";
import { LoadScriptNext } from "@react-google-maps/api";
import { useState } from "react";
import PlacesAutocomplete, { geocodeByAddress } from "react-places-autocomplete";

const colors = {
  orange: {
    50: "#FFF8F0",
    100: "#FFE0B8",
    200: "#FFC266",
    base: "#FB8E00",
    600: "#E07D00",
    700: "#B86400",
  },
  gray: {
    50: "#F5F5F5",
    100: "#E0E0E0",
    200: "#C2C2C2",
    300: "#A3A3A3",
    400: "#737373",
    base: "#0C0C0C",
  },
  danger: {
    base: "#EF4444",
    200: "#FECACA",
    700: "#B91C1C",
  },
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    paddingX: "4px",
    paddingY: "4px",
    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
    "& fieldset": {
      borderColor: colors.orange.base,
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: colors.orange[200],
    },
    "&.Mui-focused fieldset": {
      borderColor: colors.orange.base,
      boxShadow: `0 0 0 2px ${colors.orange[200]}33`,
    },
    "&.Mui-error fieldset": {
      borderColor: colors.danger.base,
    },
    "&.Mui-error.Mui-focused fieldset": {
      borderColor: colors.danger[700],
      boxShadow: `0 0 0 2px ${colors.danger[200]}55`,
    },
  },
  "& .MuiInputLabel-root": {
    color: colors.gray[400],
    fontWeight: 500,
    "&.Mui-focused": {
      color: colors.orange.base,
    },
    "&.Mui-error": {
      color: colors.danger.base,
    },
  },
  "& input": {
    padding: "12px 14px",
    fontSize: "1.0rem",
    color: colors.gray.base,
  },
};

const compactInputSx = {
  ...inputSx,
  "& .MuiOutlinedInput-root": {
    ...inputSx["& .MuiOutlinedInput-root"],
    borderRadius: "8px",
    paddingX: "2px",
    paddingY: "1px",
  },
  "& input": {
    ...inputSx["& input"],
    padding: "8px 12px",
    fontSize: "0.9rem",
  },
};

export const AddressAutocomplete = ({ onAddressSelect, initialValue = "", compact = false }) => {
  const [address, setAddress] = useState(initialValue);
  const [loading, setLoading] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(false);

  const parseAddress = async (googleAddress) => {
    try {
      setLoading(true);
      const results = await geocodeByAddress(googleAddress);

      if (results.length > 0) {
        const addressComponents = results[0].address_components;
        const formattedAddress = results[0].formatted_address;

        // Extraer componentes del address
        const getAddressComponent = (type) => {
          return addressComponents.find((component) => component.types.includes(type))?.long_name || "";
        };

        const streetNumber = getAddressComponent("street_number");
        const streetName = getAddressComponent("route");
        const city = getAddressComponent("locality");
        const state = getAddressComponent("administrative_area_level_1");
        const zipCode = getAddressComponent("postal_code");
        const country = getAddressComponent("country");

        const addressData = {
          address: `${streetNumber} ${streetName}`.trim() || formattedAddress,
          city: city,
          state: state,
          zipCode: zipCode,
          country: country,
        };

        onAddressSelect(addressData);
        setAddress(googleAddress);
        setSelectedAddress(true);
      }
    } catch (error) {
      console.error("Error parsing address:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoadScriptNext googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY} libraries={["places"]}>
      <Box sx={{ width: "100%", position: "relative" }}>
        <PlacesAutocomplete
          value={address !== "" ? address : initialValue || ""}
          onChange={(value) => {
            setAddress(value);
            setSelectedAddress(false);
          }}
          onSelect={parseAddress}
        >
          {({ getInputProps, suggestions, getSuggestionItemProps, loading: placesLoading }) => (
            <Box>
              <TextField
                {...getInputProps({
                  label: "Search Address",
                  placeholder: "Enter street address, city, or postal code",
                  variant: "outlined",
                  size: "small",
                  fullWidth: true,
                })}
                sx={compact ? compactInputSx : inputSx}
                InputProps={{
                  ...getInputProps().InputProps,
                  startAdornment: <LocationOn sx={{ mr: 1, color: colors.orange.base, fontSize: compact ? 18 : 20 }} />,
                }}
              />

              {(placesLoading || loading) && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "12px",
                    marginTop: "4px",
                  }}
                >
                  <CircularProgress size={20} sx={{ color: colors.orange.base }} />
                </Box>
              )}

              {suggestions.length > 0 && !placesLoading && !loading && (
                <Paper
                  elevation={3}
                  sx={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    marginTop: "4px",
                    borderRadius: "8px",
                    border: `1px solid ${colors.orange[200]}`,
                    overflow: "hidden",
                    maxHeight: "300px",
                    overflowY: "auto",
                  }}
                >
                  <List sx={{ padding: 0 }}>
                    {suggestions.map((suggestion, index) => (
                      <ListItemButton
                        key={index}
                        {...getSuggestionItemProps(suggestion)}
                        sx={{
                          padding: "12px 16px",
                          borderBottom: index !== suggestions.length - 1 ? `1px solid ${colors.gray[100]}` : "none",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: colors.orange[50],
                            paddingLeft: "20px",
                          },
                        }}
                      >
                        <ListItemText
                          primary={suggestion.description}
                          sx={{
                            "& .MuiListItemText-primary": {
                              fontSize: "0.95rem",
                              fontWeight: 500,
                              color: colors.gray.base,
                            },
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Paper>
              )}

              {!placesLoading && !loading && address && suggestions.length === 0 && !selectedAddress && (
                <Paper
                  elevation={3}
                  sx={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    marginTop: "4px",
                    borderRadius: "8px",
                    border: `1px solid ${colors.orange[200]}`,
                    padding: "16px",
                    textAlign: "center",
                  }}
                >
                  <Typography sx={{ color: colors.gray[400], fontSize: "0.9rem" }}>
                    No addresses found
                  </Typography>
                </Paper>
              )}
            </Box>
          )}
        </PlacesAutocomplete>
      </Box>
    </LoadScriptNext>
  );
};
